import apiService from './api';
import type { ApiResponse } from '../types';
import type { OrderRefund } from '../types/nurseryAccounts';
import type {
  CashBreakdown, CounterPaymentState, InvoiceCashPaymentResult, StripeAccountStatus, TillList, TillMovement, TillSession,
} from '../types/nurseryCounter';

/**
 * Taking payment at the counter, the tills, and the nursery's own Stripe
 * account. All under /api/nursery (HHNodeServer routes/nurseryCounter.js).
 */
class NurseryCounterService {
  // ── An order's payment ──

  async getPayment(orderId: number): Promise<ApiResponse<CounterPaymentState>> {
    return apiService.get(`/nursery/orders/${orderId}/counter-payment`);
  }

  /** Start (or show again) a card payment: the QR code the customer pays on their phone. */
  async startCard(orderId: number): Promise<ApiResponse<CounterPaymentState>> {
    return apiService.post(`/nursery/orders/${orderId}/counter-payment/card`, {});
  }

  /** Ask Stripe whether the customer has paid yet. */
  async checkCard(orderId: number): Promise<ApiResponse<CounterPaymentState>> {
    return apiService.post(`/nursery/orders/${orderId}/counter-payment/card/check`, {});
  }

  async cancelCard(orderId: number): Promise<ApiResponse<CounterPaymentState>> {
    return apiService.post(`/nursery/orders/${orderId}/counter-payment/card/cancel`, {});
  }

  /**
   * `acceptShort`: less than is due, agreed at the counter. The sale is paid in
   * full and the till is short by the difference; nothing is left owing.
   */
  async takeCash(orderId: number, sessionId: number, tendered: number, acceptShort = false): Promise<ApiResponse<CounterPaymentState>> {
    return apiService.post(`/nursery/orders/${orderId}/counter-payment/cash`, { session_id: sessionId, tendered, ...(acceptShort ? { accept_short: true } : {}) });
  }

  /** Complete a paid walk-in sale: off the lots, picked, invoiced paid. */
  async completeSale(orderId: number): Promise<ApiResponse<CounterPaymentState>> {
    return apiService.post(`/nursery/orders/${orderId}/counter-payment/complete`, {});
  }

  async takeInvoiceCash(invoiceId: number, sessionId: number, tendered: number): Promise<ApiResponse<InvoiceCashPaymentResult>> {
    return apiService.post(`/nursery/invoices/${invoiceId}/counter-payment/cash`, { session_id: sessionId, tendered });
  }

  async payCashRefund(refundId: number, sessionId: number): Promise<ApiResponse<{ refund: OrderRefund; movement: TillMovement }>> {
    return apiService.post(`/nursery/refunds/${refundId}/pay-cash`, { session_id: sessionId });
  }

  // ── Tills ──

  async listTills(): Promise<ApiResponse<TillList>> {
    return apiService.get('/nursery/tills');
  }

  async openTill(data: { till_name: string; opening_float: number; breakdown?: CashBreakdown | null; notes?: string }): Promise<ApiResponse<TillSession>> {
    return apiService.post('/nursery/tills', data);
  }

  async getTill(id: number): Promise<ApiResponse<TillSession>> {
    return apiService.get(`/nursery/tills/${id}`);
  }

  async addMovement(id: number, data: { kind: 'pay_in' | 'pay_out'; amount: number; reason: string }): Promise<ApiResponse<TillSession>> {
    return apiService.post(`/nursery/tills/${id}/movements`, data);
  }

  async bankCash(id: number, data: { amount: number; deposit_date: string; reference?: string }): Promise<ApiResponse<TillSession>> {
    return apiService.post(`/nursery/tills/${id}/bankings`, data);
  }

  async retryCashBanking(id: number): Promise<ApiResponse<{ queued: boolean }>> {
    return apiService.post(`/nursery/cash-bankings/${id}/xero`, {});
  }

  /** Retry an over, short or cash out that Xero has not taken. */
  async retryCashPosting(id: number): Promise<ApiResponse<{ queued: boolean }>> {
    return apiService.post(`/nursery/cash-postings/${id}/xero`, {});
  }

  /** Close with the count: `keep_float` stays in the drawer and the rest is banked. */
  async closeTill(id: number, data: {
    counted_cash: number; breakdown?: CashBreakdown | null; notes?: string;
    keep_float?: number; deposit_date?: string; reference?: string;
  }): Promise<ApiResponse<TillSession>> {
    return apiService.post(`/nursery/tills/${id}/close`, data);
  }

  // ── The nursery's own Stripe account ──

  async getStripeAccount(): Promise<ApiResponse<StripeAccountStatus>> {
    return apiService.get('/nursery/stripe-account');
  }

  /** A link into Stripe's own onboarding: the account is made the first time. */
  async startStripeOnboarding(): Promise<ApiResponse<{ url: string }>> {
    return apiService.post('/nursery/stripe-account/onboard', {});
  }

  async disconnectStripe(): Promise<ApiResponse<StripeAccountStatus>> {
    return apiService.delete('/nursery/stripe-account');
  }
}

const nurseryCounterService = new NurseryCounterService();
export default nurseryCounterService;
